import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Condominium } from "@/types/condominium";

interface TenantContextType {
  condominium: Condominium | null;
  loading: boolean;
  error: string | null;
  isLandingPage: boolean;
  slug: string | null;
  refreshCondominium: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType>({
  condominium: null,
  loading: true,
  error: null,
  isLandingPage: false,
  slug: null,
  refreshCondominium: async () => {},
});

/**
 * Extrai o slug do subdomínio da URL.
 * Ex: granja.condoclick.com.br → "granja"
 * Ex: condoclick.com.br → null (landing page)
 * Ex: localhost:8083 → usa query param ?tenant=granja para dev
 */
function getSlugFromHostname(): string | null {
  const hostname = window.location.hostname;
  const params = new URLSearchParams(window.location.search);

  // Detecta se é um endereço IP (v4 ou v6)
  const isIP = /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname) || hostname.includes(":");

  // Desenvolvimento local: usar query param ?tenant=granja
  if (hostname === "localhost" || hostname === "127.0.0.1" || isIP) {
    return params.get("tenant");
  }

  // Produção: extrair subdomínio
  // granja.condoclick.com.br → ["granja", "condoclick", "com", "br"]
  const parts = hostname.split(".");

  // condoclick.com.br = 3 partes → landing page
  // granja.condoclick.com.br = 4 partes → tenant "granja"
  if (parts.length > 3) {
    const sub = parts[0].toLowerCase();
    // www é apex, não é tenant
    if (sub === "www") return null;
    return sub;
  }

  // Também suportar condoclick.com (sem .br) em staging
  // condoclick.com = 2 partes → landing
  // granja.condoclick.com = 3 partes → tenant
  if (parts.length === 3 && !parts[1].includes("com")) {
    return parts[0];
  }

  return null;
}

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider = ({ children }: TenantProviderProps) => {
  const [condominium, setCondominium] = useState<Condominium | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const slug = getSlugFromHostname();
  const isLandingPage = slug === null;

  const refreshCondominium = async () => {
    if (!slug) return;
    try {
      const { data } = await supabase
        .from("condominiums")
        .select("*")
        .eq("slug", slug)
        .eq("is_active", true)
        .single();
      if (data) setCondominium(data as Condominium);
    } catch (e) {
      console.warn("refreshCondominium falhou:", e);
    }
  };

  useEffect(() => {
    if (isLandingPage) {
      if (typeof (window as any).__removeHtmlLoading === 'function') {
        (window as any).__removeHtmlLoading();
      }
      setLoading(false);
      return;
    }

    const MIN_LOADING_MS = 4000;
    const MAX_RETRIES = 3;

    const fetchCondominium = async () => {
      const startTime = Date.now();
      let lastError: unknown = null;

      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          const { data, error: fetchError } = await supabase
            .from("condominiums")
            .select("*")
            .eq("slug", slug)
            .eq("is_active", true)
            .single();

          if (!fetchError && data) {
            setCondominium(data as Condominium);
            lastError = null;
            break; // sucesso — sai do loop
          }
          lastError = fetchError;
        } catch (err) {
          lastError = err;
          console.warn(`[TenantContext] tentativa ${attempt}/${MAX_RETRIES} falhou:`, err);
        }

        if (attempt < MAX_RETRIES) {
          // Aguarda antes de tentar de novo: 1s, 2s...
          await new Promise(r => setTimeout(r, attempt * 1000));
        }
      }

      if (lastError) {
        console.error("Erro ao carregar condomínio após retentativas:", lastError);
        setError("Condomínio não encontrado");
      }

      const elapsed = Date.now() - startTime;
      const wait = Math.max(0, MIN_LOADING_MS - elapsed);
      await new Promise(r => setTimeout(r, wait));
      // Remove o único overlay de loading (o do HTML)
      if (typeof (window as any).__removeHtmlLoading === 'function') {
        (window as any).__removeHtmlLoading();
      }
      setLoading(false);
    };

    fetchCondominium();
  }, [slug, isLandingPage]);

  return (
    <TenantContext.Provider value={{ condominium, loading, error, isLandingPage, slug, refreshCondominium }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
};

export default TenantProvider;
